import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Armchair, CalendarClock, PackageSearch, Search, Smartphone } from 'lucide-react';
import Input from '../../components/shared/Input/Input';

const img = (name: string) => `${process.env.PUBLIC_URL}/images/landing/${name}`;

/* The booking flow really is a sequence, so these are numbered. */
const STEPS = [
  {
    icon: CalendarClock,
    title: 'Pick a departure',
    description: 'Search by where you board and where you get off. Every trip shows its departure time, fare and seats left.',
  },
  {
    icon: Armchair,
    title: 'Choose your seat',
    description: 'Tap a seat on the shuttle map. Taken seats are greyed out, so you know exactly where you’ll sit.',
  },
  {
    icon: Smartphone,
    title: 'Pay with M-Pesa',
    description: 'We send the payment prompt to your phone. Enter your PIN and the seat is yours.',
  },
];

const primaryLink =
  'inline-flex h-12 items-center justify-center rounded-md bg-livery-600 px-6 font-road text-[15px] font-semibold text-white shadow-[0_8px_20px_-8px_rgba(215,38,30,0.65)] transition-colors hover:bg-livery-700 active:bg-livery-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-livery-600 focus-visible:ring-offset-2';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    navigate(`/track?code=${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="font-road text-dusk-800">
      {/* Hero — navy panel bleeds into the blue-hour photo of the fleet */}
      <section className="relative bg-dusk-900 text-white">
        <div className="grid lg:min-h-[640px] lg:grid-cols-12">
          <div className="relative order-1 aspect-[4/3] sm:aspect-[16/9] lg:order-2 lg:col-span-7 lg:aspect-auto">
            <img
              src={img('hero-fleet-dusk.jpg')}
              srcSet={`${img('hero-fleet-dusk-sm.jpg')} 1000w, ${img('hero-fleet-dusk.jpg')} 2000w`}
              sizes="(min-width: 1024px) 58vw, 100vw"
              alt="Two red Cruiser shuttles parked on a Nairobi street at dusk"
              className="absolute inset-0 h-full w-full object-cover object-[35%_60%]"
              fetchPriority="high"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-dusk-900 via-dusk-900/10 to-transparent lg:bg-gradient-to-r lg:from-dusk-900 lg:via-dusk-900/20 lg:to-transparent"
            />
          </div>

          <div className="relative order-2 flex flex-col justify-center px-4 pb-28 pt-2 sm:px-8 lg:order-1 lg:col-span-5 lg:py-24 lg:pl-[max(2rem,calc((100vw-72rem)/2+1.5rem))] lg:pr-4">
            <h1 className="font-signage text-[2.9rem] font-bold uppercase leading-[0.92] tracking-[-0.01em] sm:text-6xl lg:text-[4.6rem]">
              Your seat on the next Cruiser
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-dusk-200">
              Book a numbered seat on our shuttles, pay from your phone, and
              track parcels riding the same routes.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link to="/trips" className={primaryLink}>
                Book a seat
              </Link>
              <Link
                to="/track"
                className="rounded-sm font-semibold text-white underline decoration-white/40 decoration-2 underline-offset-[6px] transition-colors hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                Track a parcel
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Action bar — the two things people come here to do */}
      <section className="relative z-10 -mt-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid overflow-hidden rounded-xl bg-white shadow-[0_24px_60px_-24px_rgba(14,26,43,0.45)] ring-1 ring-dusk-900/5 md:grid-cols-[1fr_1.35fr]">
            <div className="flex flex-col justify-between gap-5 border-b border-dusk-100 p-6 sm:p-7 md:border-b-0 md:border-r">
              <div>
                <h2 className="font-signage text-2xl font-bold uppercase tracking-tight text-dusk-900">
                  Travelling?
                </h2>
                <p className="mt-1.5 text-[15px] text-dusk-500">
                  See upcoming departures and how many seats are left on each.
                </p>
              </div>
              <Link to="/trips" className={`${primaryLink} self-start`}>
                Find a trip
              </Link>
            </div>

            <div className="p-6 sm:p-7">
              <h2 className="font-signage text-2xl font-bold uppercase tracking-tight text-dusk-900">
                Sent a parcel?
              </h2>
              <p className="mt-1.5 text-[15px] text-dusk-500">
                Enter the code on your receipt to see its latest status.
              </p>
              <form onSubmit={handleTrack} className="mt-5 flex flex-col gap-3 sm:flex-row">
                <div className="flex-1">
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="PCL-XXXXXXXX"
                    aria-label="Tracking code"
                    className="!h-12 !rounded-md !border-dusk-400 font-mono uppercase tracking-wider focus:!border-dusk-700 focus:!ring-dusk-700/15"
                    leadingIcon={<Search className="h-4 w-4" />}
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex h-12 items-center justify-center rounded-md bg-dusk-900 px-6 font-semibold text-white transition-colors hover:bg-dusk-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dusk-900 focus-visible:ring-offset-2"
                >
                  Track
                </button>
              </form>
            </div>
          </div>
          <p className="mt-4 flex items-center gap-2 px-1 text-sm text-dusk-500">
            <Smartphone className="h-4 w-4 text-dusk-400" aria-hidden />
            Pay for your seat with M-Pesa. No card needed.
          </p>
        </div>
      </section>

      {/* How booking works */}
      <section className="pb-20 pt-20 sm:pt-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="max-w-xl font-signage text-4xl font-bold uppercase leading-[0.95] tracking-tight text-dusk-900 sm:text-5xl">
            Booked in three steps
          </h2>
          <ol className="mt-12 grid gap-10 md:grid-cols-3 md:gap-8">
            {STEPS.map(({ icon: Icon, title, description }, i) => (
              <li key={title} className="relative">
                {/* The road between stops */}
                {i < STEPS.length - 1 && (
                  <span
                    aria-hidden
                    className="absolute left-14 right-0 top-6 hidden border-t-2 border-dashed border-dusk-200 md:block"
                  />
                )}
                <div className="relative flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-livery-600 text-white">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-dusk-900">
                  <span aria-hidden className="mr-2 font-signage text-livery-600">{i + 1}.</span>
                  {title}
                </h3>
                <p className="mt-2 max-w-sm leading-relaxed text-dusk-500">{description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* The fleet — the photo does the talking */}
      <section className="bg-dusk-50 py-20 sm:py-24">
        <div className="mx-auto grid max-w-6xl px-4 sm:px-6 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <figure className="lg:col-span-7">
            <img
              src={img('crew-two-shuttles.jpg')}
              srcSet={`${img('crew-two-shuttles-sm.jpg')} 1000w, ${img('crew-two-shuttles.jpg')} 2000w`}
              sizes="(min-width: 1024px) 640px, 100vw"
              alt="A Cruiser crew member standing between two red Toyota Hiace shuttles"
              loading="lazy"
              className="aspect-[3/2] w-full rounded-lg object-cover"
            />
          </figure>
          <div className="lg:col-span-5">
            <h2 className="font-signage text-4xl font-bold uppercase leading-[0.95] tracking-tight text-dusk-900 sm:text-5xl">
              The shuttle you see is the one you ride
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-dusk-600">
              Cruiser runs its own red-and-white Toyota Hiace shuttles. Each one
              has a fixed seat layout, so the seat number on your booking is the
              seat waiting for you at boarding.
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-dusk-200 pt-6">
              <div>
                <dt className="text-sm text-dusk-500">Seats</dt>
                <dd className="mt-1 text-lg font-semibold text-dusk-900">Numbered, chosen by you</dd>
              </div>
              <div>
                <dt className="text-sm text-dusk-500">Payment</dt>
                <dd className="mt-1 text-lg font-semibold text-dusk-900">M-Pesa prompt to your phone</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Parcels */}
      <section className="relative flex flex-col overflow-hidden bg-dusk-900 text-white lg:block">
        <div className="relative order-2 mx-auto w-full max-w-6xl lg:order-none">
          <div className="relative z-10 px-4 py-16 sm:px-6 sm:py-20 lg:w-1/2 lg:py-24 lg:pr-12">
            <PackageSearch className="h-8 w-8 text-chevron" aria-hidden />
            <h2 className="mt-6 font-signage text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl">
              Parcels ride with the passengers
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-dusk-200">
              Hand a parcel in at the stage and it goes on a scheduled shuttle.
              The receipt carries a tracking code — share it with whoever is
              collecting, and they can follow it here.
            </p>
            <Link
              to="/track"
              className="mt-8 inline-flex h-12 items-center justify-center rounded-md bg-white px-6 font-semibold text-dusk-900 transition-colors hover:bg-dusk-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-dusk-900"
            >
              Track a parcel
            </Link>
          </div>
        </div>
        <div className="relative order-1 aspect-[3/2] sm:aspect-[16/9] lg:absolute lg:inset-y-0 lg:right-0 lg:aspect-auto lg:w-1/2">
          <img
            src={img('rear-cruiser-co-ke.jpg')}
            srcSet={`${img('rear-cruiser-co-ke-sm.jpg')} 1000w, ${img('rear-cruiser-co-ke.jpg')} 2000w`}
            sizes="(min-width: 1024px) 50vw, 100vw"
            alt="The rear of two Cruiser shuttles showing cruiser.co.ke on the back windows"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-dusk-900 via-transparent to-transparent lg:bg-gradient-to-r lg:from-dusk-900 lg:via-dusk-900/10"
          />
        </div>
      </section>

      {/* Closing call to action */}
      <section className="bg-livery-600 py-14 text-white">
        <div className="mx-auto flex max-w-6xl px-4 sm:px-6 flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-signage text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl">
              Where are you headed?
            </h2>
            <p className="mt-2 text-lg text-white">
              Check upcoming departures and grab a seat before they go.
            </p>
          </div>
          <Link
            to="/trips"
            className="inline-flex h-12 shrink-0 items-center justify-center rounded-md bg-white px-6 font-semibold text-livery-700 transition-colors hover:bg-livery-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-livery-600"
          >
            See available trips
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
